import { type Verdict, type ExamCenterProctorRequestVerdictFragment } from '@exam-center/graphql';
import { Divider, Flex, PaperSection, Text } from '@shared/components';
import { dayjs, formatDateTime } from '@shared/utils';

import { ExamCenterProctorRequestVerdictResultCard } from '../../components';

interface ExamCenterProctorRequestVerdictHistoryProps {
  verdicts: ExamCenterProctorRequestVerdictFragment[];
}

function examCenterProctorRequestVerdictData(
  examCenterProctorRequestVerdict: ExamCenterProctorRequestVerdictFragment,
) {
  const fullName = [
    examCenterProctorRequestVerdict.updatedUser?.firstname,
    examCenterProctorRequestVerdict.updatedUser?.lastname,
  ]
    .filter(Boolean)
    .join(' ');
  const comment =
    examCenterProctorRequestVerdict.comment && examCenterProctorRequestVerdict.comment.length > 0
      ? examCenterProctorRequestVerdict.comment
      : '-';
  const submittedAt = formatDateTime(examCenterProctorRequestVerdict.submittedAt);
  const approverNumber = examCenterProctorRequestVerdict.step === 'IN_REVIEW_1' ? 1 : 2;
  const verdict = examCenterProctorRequestVerdict.verdict as Verdict;
  return { fullName, comment, submittedAt, approverNumber, verdict };
}

export default function ExamCenterProctorRequestVerdictHistory(
  props: Readonly<ExamCenterProctorRequestVerdictHistoryProps>,
) {
  const { verdicts } = props;

  const grouped = verdicts.reduce<Record<string, ExamCenterProctorRequestVerdictFragment[]>>(
    (acc, verdict) => {
      const requestKey = verdict.requestedAt.toISOString();
      const isSubmitted = verdict.submittedAt !== null;
      if (isSubmitted) {
        if (!acc[requestKey]) {
          acc[requestKey] = [];
        }
        acc[requestKey].push(verdict);
      }
      return acc;
    },
    {},
  );

  const orderedKeys = Object.keys(grouped).sort((a, b) => dayjs(a).valueOf() - dayjs(b).valueOf());
  const ordered: Record<string, ExamCenterProctorRequestVerdictFragment[]> = {};
  for (const k of orderedKeys) {
    ordered[k] = grouped[k].sort(
      (a, b) => dayjs(a.submittedAt).valueOf() - dayjs(b.submittedAt).valueOf(),
    );
  }
  return (
    <PaperSection title='ผลการอนุมัติ'>
      <Flex
        sx={{
          p: (theme) => theme.spacing(6),
          height: '100%',
          gap: (theme) => theme.spacing(6),
          flexDirection: 'column',
        }}
      >
        {Object.entries(ordered).map(([key, value], idx, arr) => (
          <>
            <Flex sx={{ flexDirection: 'column', gap: (theme) => theme.spacing(4) }}>
              <Text variant='h5'>วันที่ส่งคำขอ: {formatDateTime(new Date(key))} </Text>
              {value.map((verdict) => {
                const {
                  fullName,
                  comment,
                  submittedAt,
                  approverNumber,
                  verdict: verdictValue,
                } = examCenterProctorRequestVerdictData(verdict);
                return (
                  <ExamCenterProctorRequestVerdictResultCard
                    key={verdict.id}
                    fullName={fullName}
                    comment={comment}
                    submittedAt={submittedAt}
                    approverNumber={approverNumber}
                    verdict={verdictValue}
                  />
                );
              })}
            </Flex>
            {idx < arr.length - 1 && <Divider />}
          </>
        ))}
      </Flex>
    </PaperSection>
  );
}
